'use client';

import React from 'react';
import Modal from '../Modal';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useLocalStorageState from 'src/hooks/useLocalStorageState';
import { Close } from '@drift-labs/icons';
import Text from '../../Text/Text';
import Button from '../../Button';
import useSafePush from 'src/hooks/useSafePush';
import { twMerge } from 'tailwind-merge';
import { ChevronRight } from '@drift-labs/icons';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

const AmplifyHelpLink = ({ className }: { className?: string }) => {
	const { captureEvent } = usePostHogCapture();

	const handleClick = () => {
		captureEvent('amplify_feature_alert_learn_more');
	};

	return (
		<a
			href="https://docs.drift.trade/amplify/how-it-works"
			target="_blank"
			rel="noreferrer"
			className={twMerge('text-text-default', className)}
			onClick={handleClick}
		>
			Learn more about Amplify{' '}
			<ChevronRight className="relative top-[5px] mt-[-4px]" />
		</a>
	);
};

const AmplifyAlertModal = () => {
	const safePush = useSafePush();
	const setStore = useDriftStore((s) => s.set);
	const [_, setHasSeenAmplifyFeatureAlert] = useLocalStorageState(
		'hasSeenAmplifyFeatureAlert',
		false
	);
	const { captureEvent } = usePostHogCapture();

	const handleClose = () => {
		setHasSeenAmplifyFeatureAlert(true);
		setStore((s) => {
			s.modals.showAmplifyFeatureAlert = false;
		});
	};

	const handleViewMore = () => {
		captureEvent('amplify_feature_alert_view_more');
		handleClose();
		safePush('/earn/amplify');
	};

	return (
		<Modal onClose={handleClose}>
			<div className="pb-10 mx-5">
				<Modal.Body
					className={twMerge(
						'max-w-[384px] h-fit mx-auto mt-10 rounded-lg pb-0',
						'text-neutrals-20' // The background color of the gradient does not change with theme so we want to use a "hardcoded" colour regardless of the theme
					)}
				>
					<div
						className="w-full flex flex-col items-center justify-center py-8 px-3 sm:px-10 min-h-[220px] relative"
						style={{
							backgroundImage:
								'url(/assets/images/drift-earn/amplify-feature-alert.png)',
							backgroundSize: 'cover',
							backgroundPosition: 'center',
						}}
					>
						<div className="absolute top-3 right-3">
							<button onClick={handleClose}>
								<Close size={22} color="white" />
							</button>
						</div>
					</div>
					<div className="flex flex-col gap-4 p-4 text-text-default">
						<Text.BODY1 className="leading-5">
							Amplify your earnings up to 5x effortlessly, with the same deposit
							amount. JLP/USDC Amplify is currently available, with more
							exciting updates coming soon.
						</Text.BODY1>
						<Text.BODY1>
							<AmplifyHelpLink className="text-interactive-link" />
						</Text.BODY1>
						<Button.Secondary size="LARGE" highlight onClick={handleViewMore}>
							<Text.H3 className="normal-case">View More</Text.H3>
						</Button.Secondary>
						<Button.Secondary size="LARGE" onClick={handleClose}>
							<Text.H3 className="normal-case">Close</Text.H3>
						</Button.Secondary>
					</div>
				</Modal.Body>
			</div>
		</Modal>
	);
};

export default React.memo(AmplifyAlertModal);
